import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { supabase } from '../supabaseClient';
import { getProfileDisplayName } from '../utils/profile';
import { apiFetch } from '../utils/api';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, dbUser, refreshUser } = useAuth();
  const navigate = useNavigate();

  const displayName = useMemo(() => getProfileDisplayName(user, dbUser, ''), [user, dbUser]);
  const avatarUrl =
    user?.user_metadata?.avatar_url ||
    user?.user_metadata?.picture ||
    user?.picture;
  const profileUsername = (dbUser?.username || '').trim();
  const profileEmail = (dbUser?.email || user?.email || '').trim();

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    bio: '',
    position: '',
    daily_reminder_time: '20:00',
    journey_public: false,
    email_notifications_enabled: false,
  });
  const [saving, setSaving] = useState(false);

  const [journeyOpen, setJourneyOpen] = useState(false);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeySaving, setJourneySaving] = useState(false);
  const [journeyForm, setJourneyForm] = useState({
    journey_type: '',
    chapter_name: '',
    goal_title: '',
    goal_why: '',
    goal_category: 'revenue',
    goal_target_date: '',
    contact_name: '',
    contact_relevance: 'other',
    contact_discussion: '',
    reminder_time: '20:00',
  });

  const resetFormFromDb = useCallback(() => {
    if (!dbUser) return;
    setFormData({
      bio: dbUser.bio ?? '',
      position: dbUser.position ?? '',
      daily_reminder_time: dbUser.daily_reminder_time ?? '20:00',
      journey_public: !!dbUser.journey_public,
      email_notifications_enabled: !!dbUser.email_notifications_enabled,
    });
  }, [dbUser]);

  useEffect(() => {
    if (!dbUser || editing) return;
    resetFormFromDb();
  }, [dbUser, editing, resetFormFromDb]);

  useEffect(() => {
    if (!journeyOpen || !dbUser) return;
    let cancelled = false;
    (async () => {
      setJourneyLoading(true);
      try {
        const data = await apiFetch('/api/onboarding/current');
        if (cancelled) return;
        setJourneyForm({
          journey_type: data.chapter?.theme ?? '',
          chapter_name: data.chapter?.title ?? '',
          goal_title: data.goal?.title ?? '',
          goal_why: data.goal?.why_it_matters ?? '',
          goal_category: data.goal?.category ?? 'revenue',
          goal_target_date: data.goal?.target_date ?? '',
          contact_name: data.orbit_contact?.name ?? '',
          contact_relevance: data.orbit_contact?.relevance ?? 'other',
          contact_discussion: data.orbit_contact?.discussion ?? '',
          reminder_time: data.reminder_time ?? '20:00',
        });
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.message || 'Could not load journey setup');
        }
      } finally {
        if (!cancelled) setJourneyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [journeyOpen, dbUser]);

  const handleSaveJourney = async () => {
    setJourneySaving(true);
    try {
      await apiFetch('/api/onboarding/setup', {
        method: 'PUT',
        body: JSON.stringify({
          journey_type: journeyForm.journey_type,
          chapter_name: journeyForm.chapter_name,
          goal_title: journeyForm.goal_title,
          goal_why: journeyForm.goal_why,
          goal_category: journeyForm.goal_category,
          goal_target_date: journeyForm.goal_target_date || null,
          contact_name: journeyForm.contact_name,
          contact_relevance: journeyForm.contact_relevance,
          contact_discussion: journeyForm.contact_discussion || null,
          reminder_time: journeyForm.reminder_time,
        }),
      });
      toast.success('Journey setup updated');
      await refreshUser();
    } catch (error) {
      console.error('Journey update error:', error);
      toast.error(error?.message || 'Failed to update journey');
    } finally {
      setJourneySaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logged out successfully');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/users/profile', {
        method: 'PUT',
        body: JSON.stringify({
          bio: formData.bio,
          position: formData.position,
          daily_reminder_time: formData.daily_reminder_time,
          journey_public: formData.journey_public,
          email_notifications_enabled: formData.email_notifications_enabled,
        }),
      });
      toast.success('Settings saved');
      await refreshUser();
      setEditing(false);
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="settings-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Settings</h1>
          <p className="text-text-secondary">Manage your account and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Profile */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold text-text-primary">Profile</h2>
              {!editing && (
                <button
                  type="button"
                  onClick={() => {
                    resetFormFromDb();
                    setEditing(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gold hover:text-gold-muted transition-colors"
                >
                  Edit Profile
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-gold-light rounded-full flex items-center justify-center font-bold text-gold text-xl">
                    {(displayName || profileEmail || '?')
                      .split(/\s+/)
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-text-primary text-lg">
                    {displayName || profileEmail?.split('@')[0] || 'Your profile'}
                  </p>
                  {profileUsername ? (
                    <p className="text-sm text-gold font-mono">@{profileUsername}</p>
                  ) : null}
                  {profileEmail ? (
                    <p className="text-text-secondary text-sm">{profileEmail}</p>
                  ) : null}
                  {profileUsername ? (
                    <Link
                      to={`/u/${encodeURIComponent(profileUsername)}`}
                      className="text-xs text-gold hover:text-gold-muted mt-1 inline-block"
                    >
                      Preview how your profile looks to others
                    </Link>
                  ) : null}
                </div>
              </div>

              {editing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                      rows={3}
                      className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Position</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder="e.g., Founder & CEO"
                      className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        resetFormFromDb();
                        setEditing(false);
                      }}
                      className="px-6 py-2 border-2 border-border text-text-secondary rounded-lg hover:border-gold transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 py-2 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 text-white font-semibold rounded-lg transition-all"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {dbUser?.position ? (
                    <div>
                      <p className="text-sm text-text-secondary">Position</p>
                      <p className="text-text-primary">{dbUser.position}</p>
                    </div>
                  ) : null}
                  {dbUser?.bio ? (
                    <div>
                      <p className="text-sm text-text-secondary">Bio</p>
                      <p className="text-text-primary">{dbUser.bio}</p>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {/* Journey setup (onboarding choices) */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="font-display text-xl font-bold text-text-primary">Journey setup</h2>
                <p className="text-sm text-text-secondary mt-1">
                  Edit the chapter, goal, and key contact from onboarding — useful if you were stuck
                  halfway or want to adjust your answers.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setJourneyOpen((o) => !o)}
                className="shrink-0 px-4 py-2 text-sm font-medium text-gold hover:text-gold-muted transition-colors"
              >
                {journeyOpen ? 'Hide' : 'Edit'}
              </button>
            </div>
            {!dbUser?.current_chapter_id && (
              <p className="text-sm text-text-tertiary">
                No chapter yet.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/onboarding')}
                  className="text-gold hover:underline"
                >
                  Continue onboarding
                </button>{' '}
                to create one, then you can fine-tune here.
              </p>
            )}
            {journeyOpen && dbUser?.current_chapter_id && (
              <div className="space-y-4 pt-2 border-t border-border mt-4">
                {journeyLoading ? (
                  <p className="text-sm text-text-tertiary font-mono">Loading…</p>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Chapter title</label>
                      <input
                        type="text"
                        value={journeyForm.chapter_name}
                        onChange={(e) => setJourneyForm({ ...journeyForm, chapter_name: e.target.value })}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Journey type (theme)</label>
                      <input
                        type="text"
                        value={journeyForm.journey_type}
                        onChange={(e) => setJourneyForm({ ...journeyForm, journey_type: e.target.value })}
                        placeholder="e.g. startup, freelance"
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Goal title</label>
                      <input
                        type="text"
                        value={journeyForm.goal_title}
                        onChange={(e) => setJourneyForm({ ...journeyForm, goal_title: e.target.value })}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Why it matters</label>
                      <textarea
                        value={journeyForm.goal_why}
                        onChange={(e) => setJourneyForm({ ...journeyForm, goal_why: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">Goal category</label>
                        <select
                          value={journeyForm.goal_category}
                          onChange={(e) => setJourneyForm({ ...journeyForm, goal_category: e.target.value })}
                          className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                        >
                          <option value="revenue">Revenue</option>
                          <option value="product">Product</option>
                          <option value="growth">Growth</option>
                          <option value="health">Health</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">Target date</label>
                        <input
                          type="date"
                          value={journeyForm.goal_target_date || ''}
                          onChange={(e) => setJourneyForm({ ...journeyForm, goal_target_date: e.target.value })}
                          className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Key contact name</label>
                      <input
                        type="text"
                        value={journeyForm.contact_name}
                        onChange={(e) => setJourneyForm({ ...journeyForm, contact_name: e.target.value })}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Contact relevance</label>
                      <select
                        value={journeyForm.contact_relevance}
                        onChange={(e) => setJourneyForm({ ...journeyForm, contact_relevance: e.target.value })}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      >
                        <option value="mentor">Mentor</option>
                        <option value="peer">Peer</option>
                        <option value="investor">Investor</option>
                        <option value="customer">Customer</option>
                        <option value="family">Family</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">What you discuss</label>
                      <textarea
                        value={journeyForm.contact_discussion}
                        onChange={(e) => setJourneyForm({ ...journeyForm, contact_discussion: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Daily reminder time</label>
                      <input
                        type="time"
                        value={journeyForm.reminder_time}
                        onChange={(e) => setJourneyForm({ ...journeyForm, reminder_time: e.target.value })}
                        className="px-4 py-2 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveJourney}
                      disabled={journeySaving}
                      className="px-6 py-2 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 text-white font-semibold rounded-lg transition-all"
                    >
                      {journeySaving ? 'Saving…' : 'Save journey setup'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Privacy */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <h2 className="font-display text-xl font-bold text-text-primary mb-4">Privacy</h2>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium text-text-primary">Public Journey</p>
                <p className="text-sm text-text-secondary">
                  Allow other Daypoppers to see your stats and recent entry titles (not full entries)
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={formData.journey_public}
                  onChange={(e) => setFormData({ ...formData, journey_public: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gold/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-bg-surface after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gold" />
              </label>
            </div>
            <p className="text-xs text-text-tertiary mt-3">
              Use &ldquo;Save settings&rdquo; below to apply privacy and reminder changes.
            </p>
          </div>

          {/* Plan */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <h2 className="font-display text-xl font-bold text-text-primary mb-4">Plan</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-text-primary mb-1">
                  {(dbUser?.plan || 'free') === 'free' ? 'Free plan' : 'Pro'}
                </p>
                <p className="text-sm text-text-secondary">
                  {(dbUser?.plan || 'free') === 'free'
                    ? 'Unlimited entries, goals, and contacts'
                    : 'Full access to all features'}
                </p>
              </div>
              {(dbUser?.plan || 'free') === 'free' && (
                <button
                  type="button"
                  className="px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <h2 className="font-display text-xl font-bold text-text-primary mb-4">Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-text-primary">Email Notifications</p>
                  <p className="text-sm text-text-secondary">Receive daily reminders and updates via email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.email_notifications_enabled}
                    onChange={(e) => setFormData({ ...formData, email_notifications_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gold/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-bg-surface after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gold" />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Reminder Time</label>
                <input
                  type="time"
                  value={formData.daily_reminder_time}
                  onChange={(e) => setFormData({ ...formData, daily_reminder_time: e.target.value })}
                  className="px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                />
              </div>
              <p className="text-xs text-text-tertiary italic">
                Email delivery will be enabled once the custom domain is connected.
                Your preferences are saved and ready.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 text-white font-semibold rounded-xl transition-all"
              data-testid="save-settings-button"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>

          {/* Logout */}
          <div className="bg-bg-surface rounded-2xl p-6 border border-border shadow-sm">
            <h2 className="font-display text-xl font-bold text-text-primary mb-4">Account</h2>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-error hover:bg-error/90 text-white font-semibold rounded-xl transition-all"
              data-testid="logout-button"
            >
              <LogOut size={20} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
